import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import ReloadOutlined from '@ant-design/icons/ReloadOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';

// ==============================|| ELEMENT ERROR - COMMON ||============================== //

// Stale code-split chunk after a deploy/rebuild: the old chunk URL 404s. A reload
// fetches the new bundle and fixes it, so we auto-reload ONCE (rate-limited so a
// genuinely-missing module can't loop).
const CHUNK_ERROR_RE = /dynamically imported module|failed to fetch|loading chunk|chunkloaderror|importing a module script failed/i;
const RELOAD_KEY = 'ayro:lastChunkReload';

export default function ErrorBoundary() {
  const error = useRouteError();
  const theme = useTheme();

  // Friendly title + the route-response code, when present.
  let title = 'Something went wrong';
  let detail = '';
  let status = null;

  if (isRouteErrorResponse(error)) {
    status = error.status;
    const byStatus = {
      404: "This page doesn't exist.",
      401: "You aren't authorized to see this.",
      403: "You don't have permission to access this.",
      503: 'Our service is temporarily unavailable. Please try again shortly.'
    };
    title = `Error ${error.status}`;
    detail = byStatus[error.status] || error.statusText || 'Unexpected error.';
  } else if (error instanceof Error) {
    detail = error.message;
  } else if (typeof error === 'string') {
    detail = error;
  }

  const isChunkError = CHUNK_ERROR_RE.test(detail || '');

  useEffect(() => {
    if (!isChunkError) return;
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    // Auto-reload at most once per 10s — recovers from a stale bundle without
    // looping if the module is truly gone.
    if (Date.now() - last > 10000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    }
  }, [isChunkError]);

  const friendlyMessage = isChunkError
    ? 'A new version of the app may have just been deployed. Reloading usually fixes this.'
    : detail || 'An unexpected error occurred while loading this page.';

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Paper variant="outlined" sx={{ maxWidth: 560, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 2, textAlign: 'center' }}>
        <Stack spacing={2.5} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: 'error.main'
            }}
          >
            <WarningOutlined style={{ fontSize: '1.9rem' }} />
          </Box>

          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {status ? title : isChunkError ? 'Update available' : title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {friendlyMessage}
            </Typography>
          </Box>

          {/* Technical detail — present but de-emphasized for support/debugging. */}
          {detail && (
            <Box
              component="pre"
              sx={{
                width: '100%',
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'grey.100',
                color: 'text.secondary',
                fontSize: '0.75rem',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 160,
                overflow: 'auto'
              }}
            >
              {detail}
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }} justifyContent="center">
            <Button variant="contained" startIcon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              Reload page
            </Button>
            <Button variant="outlined" color="inherit" startIcon={<HomeOutlined />} onClick={() => (window.location.href = '/')}>
              Back to home
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
